import React, { useEffect, useState } from 'react'
import { runInAction } from 'mobx'
import PropTypes from 'prop-types'
import { observer } from 'mobx-react-lite'
import { Button, Popconfirm, Popover, Select, Space, Tag } from 'antd'
import { InfoOutlined, CalendarOutlined } from '@ant-design/icons'
import { v4 as uuidv4 } from 'uuid'
import { useStores } from '../../../cls/stores/MobXProvider'
import {
	Filter,
	UserString,
	LstTable,
	FormatedTimestamp,
	CustomTable
} from '../../../components/Shared'
import { Link, useNavigate } from 'react-router-dom'
import {
	GeneralContentWrapper,
	GeneralTourTitle
} from '../../../components/Shared/GeneralComponents'
import { getTourArrayByKeys } from '../../../stores/tour/config'
import {
	actSaveMeeting,
	newMeeting,
	requireMeetingById
} from '../../../stores/meetings/actions'
import {
	actLoadMeetings,
	actUndoOrDeleteMeeting
} from '../../../stores/ui-lst-meetings/actions'
import dayjs from 'dayjs'
import { translate as t } from '../../../cls/TranslationContext'
import { useSmartTranslation } from '../../../cls/TranslationContext'

export const meetingProgressOptions = [
	{
		key: 'by-progress',
		value: 'pending, confirmed',
		label: t('__by%single.status')
	},
	{
		key: 'by-progress-pending',
		value: 'pending',
		label: t('single.pending')
	},
	{
		key: 'by-progress-confirmed',
		label: t('single.confirmed'),
		value: 'confirmed'
	},
	{
		key: 'by-progress-cancelled',
		value: 'cancelled',
		label: t('single.canceled')
	}
]

/**
 * @function LstItem
 * @description Renders a single table row representing a meeting, including action controls, tag status, and meeting metadata in a popover.
 * @param {Object} props
 * @param {string} props.id - The unique identifier for the meeting item.
 * @param {boolean} props.isFirst - Whether this item is the first in the list (used for guided tour targeting).
 */
const LstItem = observer(({ id, isFirst }) => {
	const navigate = useNavigate()
	const _itm = requireMeetingById(id)
	const { t } = useSmartTranslation()
	const [showConfirmUndoOrDelete, setConfirmUndoOrDelete] = useState(false)
	/**
	 * @function confirmUndoOrDelete
	 * @description Executes deletion or undeletion of a meeting item by ID, based on its current deletion state.
	 */
	const confirmUndoOrDelete = () => {
		setConfirmUndoOrDelete(false)
		actUndoOrDeleteMeeting(id, !!_itm.to_delete).catch(console.error)
	}

	const _actions = [
		{
			key: 'view',
			label: t('single.view'),
			value: 'view'
		},
		{
			key: 'data',
			label: t('__edit%'),
			value: 'data',
			disabled: !!_itm.to_delete
		}
	]

	if (_itm.to_delete) {
		_actions.push({
			key: 'undo-del',
			label: t('single.delete-undo'),
			value: 'undo-delete'
		})
	} else {
		_actions.push({
			key: 'del',
			label: t('single.delete'),
			value: 'delete'
		})
	}

	/**
	 * @function handleActions
	 * @description Handles action selection (view, edit, delete, undo-delete) from the dropdown for a specific meeting entry.
	 * @param {string} value - Selected action key from the dropdown.
	 */
	const handleActions = (value) => {
		switch (value) {
			case 'view':
				navigate(`${id}/view`)
				break
			case 'data':
				navigate(`${id}/edit`)
				break
			case 'undo-delete':
				setConfirmUndoOrDelete(true)
				break
			case 'delete':
				setConfirmUndoOrDelete(true)
				break
			default:
				break
		}
	}

	return (
		<tr>
			<td>
				<Link to={`${id}/view`} id={isFirst ? 'target-itm-link' : ''}>
					{_itm.name}
				</Link>
			</td>
			<td id={isFirst ? 'target-email' : ''}>{_itm.email}</td>
			<td id={isFirst ? 'target-phone' : ''}>{_itm.phone}</td>
			<td id={isFirst ? 'target-date' : ''}>{_itm.date}</td>
			<td id={isFirst ? 'target-hour' : ''}>{_itm.hour}</td>
			<td id={isFirst ? 'target-progress' : ''}>
				<Tag
					color={
						_itm.progress === 'pending'
							? 'warning'
							: _itm.progress === 'confirmed'
							? 'success'
							: _itm.progress === 'cancelled'
							? 'error'
							: 'default'
					}
				>
					{_itm.progress}
				</Tag>
			</td>
			<td>
				<Space
					style={{ width: '100%' }}
					id={isFirst ? 'target-itm-actions' : ''}
					align="middle"
				>
					<Popover
						id={isFirst ? 'target-itm-info' : ''}
						content={() => (
							<CustomTable
								data={[
									[
										{
											item: (
												<strong>
													{t('__info%_meeting')}
												</strong>
											)
										}
									],
									[
										t('single.created-at'),
										{
											item: (
												<FormatedTimestamp
													timestamp={_itm.createdTs}
													formatedAs="DD.MM.YYYY hh:mm"
												/>
											)
										}
									],
									[
										t('single.created-by.label'),
										{
											item: _itm.createdBy ? (
												<UserString
													userId={_itm.createdBy}
													path="name"
												/>
											) : (
												<span>
													{t(
														'single.external-booking'
													)}
												</span>
											)
										}
									]
								]}
							/>
						)}
						trigger="hover"
					>
						<Button icon={<InfoOutlined />} />
					</Popover>
					<Space style={{ width: '100%' }} direction="vertical">
						<Select
							id={isFirst ? 'target-itm-actions' : ''}
							value={t('single.actions')}
							options={_actions}
							onChange={handleActions}
							style={{ width: 120 }}
						/>
						<Popconfirm
							title={
								_itm.to_delete
									? t('single.confirm-undo-delete')
									: t('single.confirm-delete')
							}
							open={showConfirmUndoOrDelete}
							onConfirm={confirmUndoOrDelete}
							onCancel={() => setConfirmUndoOrDelete(false)}
							okType="link"
							placement="topLeft"
						/>
					</Space>
				</Space>
				{_itm.to_delete && (
					<div>
						<span>
							{t('single.rejected-on')}
							<FormatedTimestamp timestamp={_itm.to_delete} />
						</span>
					</div>
				)}
			</td>
		</tr>
	)
})
LstItem.propTypes = {
	id: PropTypes.string.isRequired
}

/**
 * @component MeetingLst
 * @description Displays a list of scheduled meetings with actions to view, edit, delete, or undo deletion. Supports filtering, guided UI tours, and calendar view redirection.
 */
const MeetingLst = observer(() => {
	const navigate = useNavigate()
	const { uiLstMeetings, appType, users, tourStore } = useStores()
	const { loggedInUserId } = users

	const bySearch = uiLstMeetings.search
	const byProgress = uiLstMeetings.filter.progress
	const byShowAll = uiLstMeetings.filter.show_all
	const { t } = useSmartTranslation()

	useEffect(() => {
		actLoadMeetings()
	}, [bySearch, byProgress, byShowAll, appType.appIs])

	const meetingLst = getTourArrayByKeys('meetingLst', ['all'])
	const tourFilter = [
		...getTourArrayByKeys('filter', [
			'section',
			'search',
			'progress',
			'new'
		])
	]

	/**
	 * @function startTour
	 * @description Starts the onboarding/tour flow for the meetings list screen. If no items are available, a dummy item is injected to enable step progression.
	 */
	const startTour = () => {
		const hasItems = uiLstMeetings.ids.length > 0
		if (!hasItems) {
			const newId = uuidv4()
			tourStore.setDeleteItemAct(() => {
				runInAction(() => {
					uiLstMeetings.setIds(
						uiLstMeetings.ids.filter((id) => id !== newId)
					)
					uiLstMeetings.setNr(uiLstMeetings.nr - 1)
				})
			})

			const demoObj = {
				id: newId,
				name: 'John Doe',
				email: 'john@example.com',
				phone: '+123456789',
				date: dayjs().format('YYYY-MM-DD'),
				hour: '10:00 AM',
				progress: 'pending',
				createdBy: loggedInUserId,
				createdTs: dayjs().unix()
			}
			actSaveMeeting(newId, demoObj, true).catch(console.error)
		}
		tourStore.startTour('meetingLst-tour', [...tourFilter, ...meetingLst])
	}

	return (
		<GeneralContentWrapper>
			<GeneralTourTitle
				titleProps={{ text: t('_meeting.multiple') }}
				buttonProps={{
					onClick: startTour
				}}
			/>
			<Button
				className="no-print"
				id="target-calendar-view"
				type="primary"
				icon={<CalendarOutlined />}
				style={{ marginBottom: 16 }}
				onClick={() => navigate('calendar')}
			>
				{t('single.calendar.go-to')}
			</Button>
			<Filter
				uiStore={uiLstMeetings}
				isForNotification={true}
				addNew={() => {
					const newId = uuidv4()
					newMeeting(newId).then(() => {
						navigate(`/meeting/setter`)
					})
				}}
				hide={[
					'priority',
					'category',
					'by-user',
					'for-user',
					'orderBy',
					'deleted',
					'entity_table',
					'kind',
					'show_all'
				]}
				progressOptions={meetingProgressOptions}
			/>
			<LstTable
				LstItem={LstItem}
				store={uiLstMeetings}
				className="table-pro"
			>
				<tr id="items-lst">
					<th id="target-meeting">{t('single.name')}</th>
					<th id="target-email">{t('single.email')}</th>
					<th id="target-phone">{t('single.phone')}</th>
					<th id="target-date">{t('single.date')}</th>
					<th id="target-hour">{t('single.time')}</th>
					<th id="target-progress">{t('single.status')}</th>
					<th id="target-actions">{t('single.actions')}</th>
				</tr>
			</LstTable>
		</GeneralContentWrapper>
	)
})

export default MeetingLst
